import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette } from "@fortawesome/free-solid-svg-icons";
import {
  getTeacherQuiz,
  patchTeacherQuiz,
  type TeacherQuizQuestion,
  type TeacherQuizRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { AutoResizeTextarea } from "../components/AutoResizeTextarea";
import { QuizRichText } from "../components/QuizRichText";
import { LessonIconPickerModal } from "./LessonIconPickerModal";
import { QuizIcon } from "./QuizIcon";

function showsRichPreview(text: string): boolean {
  const t = text.trim();
  return t.includes("\n") || t.includes("```") || t.includes("`");
}

export default function TeacherQuizEditorPage() {
  const { courseId, quizId } = useParams<{ courseId: string; quizId: string }>();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [quiz, setQuiz] = useState<TeacherQuizRecord | null>(null);
  const [savedJson, setSavedJson] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [expandedReview, setExpandedReview] = useState<Record<string, boolean>>({});

  const dirty = quiz !== null && savedJson !== null && JSON.stringify(quiz) !== savedJson;

  const load = useCallback(async () => {
    if (!bearer || !courseId || !quizId) return;
    setLoading(true);
    setError(null);
    try {
      const { quiz: q } = await getTeacherQuiz(bearer, courseId, quizId);
      setQuiz(q);
      setSavedJson(JSON.stringify(q));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load quiz");
    } finally {
      setLoading(false);
    }
  }, [bearer, courseId, quizId]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateQuestion(index: number, patch: Partial<TeacherQuizQuestion>) {
    setQuiz((prev) => {
      if (!prev) return prev;
      const questions = [...prev.questions];
      questions[index] = { ...questions[index]!, ...patch };
      return { ...prev, questions };
    });
  }

  function toggleReview(questionId: string) {
    setExpandedReview((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  }

  async function handleSave() {
    if (!bearer || !courseId || !quizId || !quiz) return;
    setSaving(true);
    setError(null);
    try {
      const { quiz: saved } = await patchTeacherQuiz(bearer, courseId, quizId, {
        title: quiz.title,
        description: quiz.description,
        displayQuestionCount: quiz.displayQuestionCount,
        questions: quiz.questions,
        iconName: quiz.iconName,
        iconBgColor: quiz.iconBgColor,
        iconFgColor: quiz.iconFgColor,
      });
      setQuiz(saved);
      setSavedJson(JSON.stringify(saved));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!courseId || !quizId) {
    return (
      <div className="teacher-quiz-editor teacher-pad">
        <p className="teacher-panel-muted">Missing course or quiz.</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="teacher-quiz-editor teacher-pad">
        <p className="teacher-panel-muted teacher-quiz-loading">Loading quiz…</p>
      </div>
    );
  }
  if (!quiz) {
    return (
      <div className="teacher-quiz-editor teacher-pad">
        <div className="warn-banner" role="alert">
          {error ?? "Quiz not found"}
        </div>
      </div>
    );
  }

  const previewUrl = `/teacher/courses/${encodeURIComponent(courseId)}/quizzes/${encodeURIComponent(quizId)}/preview`;

  return (
    <div className="teacher-quiz-editor teacher-pad">
      <header className="teacher-quiz-editor-head">
        <button
          type="button"
          className="teacher-quiz-icon-btn"
          title="Change quiz icon"
          onClick={() => setIconPickerOpen(true)}
        >
          <QuizIcon
            quizId={quiz.id}
            title={quiz.title}
            iconName={quiz.iconName}
            bgColor={quiz.iconBgColor}
            fgColor={quiz.iconFgColor}
          />
          <span className="teacher-quiz-icon-btn-badge" aria-hidden>
            <FontAwesomeIcon icon={faPalette} />
          </span>
        </button>
        <div className="teacher-quiz-editor-head-copy">
          <h1 className="teacher-panel-title">Edit quiz</h1>
          <p className="teacher-panel-lede">
            Pool of {quiz.questions.length} questions · {quiz.displayQuestionCount} shown per attempt
          </p>
        </div>
      </header>

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      <section className="teacher-quiz-editor-section">
        <div className="teacher-field">
          <label htmlFor="quiz-title" className="teacher-field-label">
            Title
          </label>
          <AutoResizeTextarea
            id="quiz-title"
            className="teacher-input"
            minRows={1}
            maxRows={3}
            value={quiz.title}
            onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
          />
        </div>

        <div className="teacher-field">
          <label htmlFor="quiz-description" className="teacher-field-label">
            Description
          </label>
          <AutoResizeTextarea
            id="quiz-description"
            className="teacher-input"
            minRows={2}
            maxRows={6}
            value={quiz.description ?? ""}
            onChange={(e) => setQuiz({ ...quiz, description: e.target.value })}
          />
        </div>

        <div className="teacher-field teacher-field--narrow">
          <label htmlFor="quiz-display-count" className="teacher-field-label">
            Questions per attempt
          </label>
          <input
            id="quiz-display-count"
            className="teacher-input"
            type="number"
            min={1}
            max={20}
            value={quiz.displayQuestionCount}
            onChange={(e) =>
              setQuiz({ ...quiz, displayQuestionCount: Number.parseInt(e.target.value, 10) || 5 })
            }
          />
        </div>
      </section>

      <div className="teacher-quiz-questions">
        {quiz.questions.map((q, qi) => {
          const reviewOpen =
            expandedReview[q.id] ||
            Boolean(q.rationale?.trim()) ||
            (q.optionExplanations?.some((x) => x.trim()) ?? false);
          const promptFieldId = `quiz-q-${q.id}-prompt`;
          return (
            <article key={q.id} className="teacher-quiz-question-card">
              <h3 className="teacher-quiz-question-heading">Question {qi + 1}</h3>

              <div className="teacher-field">
                <label htmlFor={promptFieldId} className="teacher-field-label">
                  Prompt
                </label>
                <p className="teacher-field-hint">
                  New lines are preserved. Wrap code in triple backticks for a monospace block. Use single backticks
                  for inline code.
                </p>
                <AutoResizeTextarea
                  id={promptFieldId}
                  className="teacher-input"
                  minRows={3}
                  maxRows={12}
                  value={q.prompt}
                  onChange={(e) => updateQuestion(qi, { prompt: e.target.value })}
                />
                {showsRichPreview(q.prompt) ? (
                  <div className="teacher-quiz-live-preview" aria-label="Prompt preview">
                    <span className="teacher-quiz-live-preview-label">Preview</span>
                    <QuizRichText text={q.prompt} />
                  </div>
                ) : null}
              </div>

              <fieldset className="teacher-quiz-options">
                <legend className="teacher-quiz-options-legend">Answer options</legend>
                {q.options.map((opt, oi) => {
                  const optionId = `quiz-q-${q.id}-opt-${oi}`;
                  return (
                    <div
                      key={oi}
                      className={`teacher-quiz-option-edit${q.correctOptionIndex === oi ? " is-correct" : ""}`}
                    >
                      <div className="teacher-quiz-option-edit-head">
                        <input
                          type="radio"
                          id={`correct-${q.id}-${oi}`}
                          name={`correct-${q.id}`}
                          checked={q.correctOptionIndex === oi}
                          onChange={() => updateQuestion(qi, { correctOptionIndex: oi })}
                        />
                        <label htmlFor={optionId} className="teacher-field-label">
                          Option {oi + 1}
                          {q.correctOptionIndex === oi ? (
                            <span className="teacher-quiz-correct-tag">Correct</span>
                          ) : null}
                        </label>
                      </div>
                      <AutoResizeTextarea
                        id={optionId}
                        className="teacher-input"
                        minRows={2}
                        maxRows={8}
                        value={opt}
                        onChange={(e) => {
                          const options = [...q.options];
                          options[oi] = e.target.value;
                          updateQuestion(qi, { options });
                        }}
                      />
                      {showsRichPreview(opt) ? (
                        <div className="teacher-quiz-live-preview" aria-label={`Option ${oi + 1} preview`}>
                          <span className="teacher-quiz-live-preview-label">Preview</span>
                          <QuizRichText text={opt} />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </fieldset>

              <div className="teacher-quiz-review-section">
                <button
                  type="button"
                  className="btn ghost tight teacher-quiz-review-toggle"
                  onClick={() => toggleReview(q.id)}
                >
                  {reviewOpen ? "Hide teacher notes" : "Teacher notes (optional)"}
                </button>
                {reviewOpen ? (
                  <div className="teacher-quiz-review-fields">
                    <div className="teacher-field">
                      <label htmlFor={`rationale-${q.id}`} className="teacher-field-label">
                        Why this question?
                      </label>
                      <AutoResizeTextarea
                        id={`rationale-${q.id}`}
                        className="teacher-input"
                        minRows={2}
                        maxRows={6}
                        value={q.rationale ?? ""}
                        placeholder="Rationale for including this question (students never see this)"
                        onChange={(e) => updateQuestion(qi, { rationale: e.target.value })}
                      />
                    </div>
                    {q.options.map((_, oi) => (
                      <div key={oi} className="teacher-field">
                        <label htmlFor={`explain-${q.id}-${oi}`} className="teacher-field-label">
                          Option {oi + 1} explanation
                        </label>
                        <AutoResizeTextarea
                          id={`explain-${q.id}-${oi}`}
                          className="teacher-input"
                          minRows={2}
                          maxRows={6}
                          value={q.optionExplanations?.[oi] ?? ""}
                          placeholder="Why this option is right or wrong"
                          onChange={(e) => {
                            const optionExplanations = [
                              ...(q.optionExplanations ?? Array(q.options.length).fill("")),
                            ];
                            while (optionExplanations.length < q.options.length) optionExplanations.push("");
                            optionExplanations[oi] = e.target.value;
                            updateQuestion(qi, { optionExplanations });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      <div className="teacher-quiz-editor-actions">
        <button
          type="button"
          className="btn primary"
          disabled={saving || !dirty}
          onClick={() => void handleSave()}
        >
          {saving ? "Saving…" : "Save quiz"}
        </button>
        <span
          className={`teacher-quiz-save-status${dirty ? " is-dirty" : ""}`}
          role="status"
          aria-live="polite"
        >
          {saving ? "Saving…" : dirty ? "Unsaved changes" : "All changes saved"}
        </span>
        <Link className="btn secondary" to={previewUrl}>
          Preview
        </Link>
        <Link className="btn secondary" to={`/teacher/courses/${encodeURIComponent(courseId)}/manage`}>
          Back to course
        </Link>
      </div>

      <LessonIconPickerModal
        open={iconPickerOpen}
        heading="Choose quiz icon"
        selectedIconName={quiz.iconName ?? undefined}
        bgColor={quiz.iconBgColor ?? "#1b4d2e"}
        fgColor={quiz.iconFgColor ?? "#fdf9f3"}
        lessonId={quiz.id}
        title={quiz.title}
        onClose={() => setIconPickerOpen(false)}
        onSelect={(iconName) => {
          setQuiz((prev) =>
            prev
              ? {
                  ...prev,
                  iconName: iconName ?? "clipboard-question",
                  iconBgColor: prev.iconBgColor ?? "#1b4d2e",
                  iconFgColor: prev.iconFgColor ?? "#fdf9f3",
                }
              : prev
          );
        }}
      />
    </div>
  );
}
